import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Download, FileUp, Layers, Send, Sparkles, XCircle } from "lucide-react";
import { toast } from "sonner";
import { TESTIDS } from "@/constants/testIds";
import { Link } from "react-router-dom";

const LABELS = { ingest: "Ingest", normalize: "Normalize", extract: "Extract", review: "Review", publish: "Publish" };
const STATUS = {
  queued: ["sev-info", Sparkles],
  completed: ["sev-ok", CheckCircle2],
  published: ["sev-ok", CheckCircle2],
  reviewed: ["sev-ok", CheckCircle2],
  failed: ["sev-critical", XCircle],
  awaiting_review: ["sev-warning", AlertTriangle],
  pending: ["sev-info", AlertTriangle],
  processing: ["sev-info", Sparkles],
};
const ACTIVE_STATUSES = new Set(["queued", "processing"]);

function pipelineErrorMessage(error) {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") return detail;
  const responseText = typeof error.response?.data === "string" ? error.response.data : "";
  const status = error.response?.status;
  if ([502, 503, 504].includes(status) || /cloudflare|malformed headers|origin web server|bad gateway/i.test(responseText)) {
    return "The upload gateway lost the backend response. Retry once; if it recurs, check the backend and object-storage logs.";
  }
  if (!error.response) {
    return "The backend did not respond to the source-package upload. Check the connection and retry.";
  }
  return "Map processing failed";
}

function StatusBadge({ status }) {
  const [cls, Icon] = STATUS[status] || ["sev-info", AlertTriangle];
  return <span data-testid={TESTIDS.pipelineStatusBadge} className={`${cls} inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest`}><Icon size={10} />{status}</span>;
}

function StageRow({ name, stage }) {
  const [expanded, setExpanded] = useState(false);
  const details = Object.fromEntries(Object.entries(stage || {}).filter(([key]) => !["status", "errors", "updated_at"].includes(key)));
  return (
    <div data-testid={TESTIDS.pipelinePhaseRow} data-stage={name} className="border border-[#1E293B] bg-[#0E1522]">
      <button onClick={() => setExpanded(value => !value)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#162032]">
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="font-mono text-[10px] text-slate-500 w-20 uppercase">{name}</span>
        <span className="text-sm text-slate-200 flex-1">{LABELS[name] || name}</span>
        {(stage.errors || []).length > 0 && <span className="text-[10px] text-red-400">{stage.errors.length} error(s)</span>}
        <StatusBadge status={stage.status} />
      </button>
      {expanded && (
        <div className="border-t border-[#1E293B] px-3 py-2 bg-[#050B14] text-[11px] font-mono">
          {Object.keys(details).length > 0 && <pre className="text-slate-400 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(details, null, 2)}</pre>}
          {(stage.errors || []).map((error, index) => <div key={index} className="text-red-400">• {error}</div>)}
        </div>
      )}
    </div>
  );
}

export default function MapPipelinePanel({ terminalId, onMapReady, onPublished }) {
  const [files, setFiles] = useState([]);
  const [sourceType, setSourceType] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const announcedRef = useRef(null);
  const mapReadyRef = useRef(null);

  const summary = useMemo(() => {
    const stages = Object.values(result?.stages || {});
    return { total: stages.length, completed: stages.filter(stage => stage.status === "completed").length };
  }, [result]);

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    setBusy(false);
    if (!terminalId) return () => { cancelled = true; };
    api.latestPipeline(terminalId).then(job => {
      if (!cancelled) {
        setResult(job);
        setBusy(ACTIVE_STATUSES.has(job.status));
      }
    }).catch(error => {
      if (!cancelled && error.response?.status !== 404) {
        toast.error("Could not restore the latest map workflow");
      }
    });
    return () => { cancelled = true; };
  }, [terminalId]);

  useEffect(() => {
    if (!terminalId || !result?.id || !ACTIVE_STATUSES.has(result.status)) {
      setBusy(false);
      return undefined;
    }
    setBusy(true);
    let cancelled = false;
    let polling = false;
    const timer = window.setInterval(async () => {
      if (polling) return;
      polling = true;
      try {
        const job = await api.pipelineStatus(terminalId, result.id);
        if (!cancelled) setResult(job);
      } catch (error) {
        if (!cancelled && error.response?.status === 404) {
          setBusy(false);
          window.clearInterval(timer);
          toast.error("Map workflow could not be recovered");
        }
      } finally {
        polling = false;
      }
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [terminalId, result?.id, result?.status]);

  useEffect(() => {
    if (!result?.id) return;
    if (result.map_id && mapReadyRef.current !== `${result.id}:${result.map_id}`) {
      mapReadyRef.current = `${result.id}:${result.map_id}`;
      if (onMapReady) onMapReady(result.map_id);
    }
    if (result.status === "awaiting_review" && announcedRef.current !== `${result.id}:review`) {
      announcedRef.current = `${result.id}:review`;
      toast.success(`${result.stages?.extract?.annotation_count || 0} proposals ready for review`);
    }
    if (result.status === "failed" && announcedRef.current !== `${result.id}:failed`) {
      announcedRef.current = `${result.id}:failed`;
      const errors = result.stages?.[result.current_stage]?.errors || [];
      toast.error(errors[0] || "Map processing failed");
    }
  }, [onMapReady, result]);

  const run = async () => {
    if (!files.length || !terminalId) return toast.error("Choose a source package and terminal");
    setBusy(true);
    try {
      const response = await api.processMapPipeline(terminalId, files, sourceType);
      setResult(response);
      toast.success("Source package accepted; processing will continue in the background");
    } catch (error) {
      toast.error(pipelineErrorMessage(error));
      setBusy(false);
    }
  };

  const reviewAll = async (action) => {
    try {
      const response = await api.reviewPipeline(result.id, action);
      setResult(response.pipeline);
      toast.success(`${response.modified} annotation(s) ${action}ed`);
    } catch (error) { toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || "Review failed"); }
  };

  const publish = async () => {
    try {
      const response = await api.publishPipeline(result.id);
      setResult(response);
      if (onPublished) onPublished(response.map_id);
      toast.success("Map version published");
    } catch (error) { toast.error(error.response?.data?.detail || "Publish failed"); }
  };

  const exportGeoJson = async () => {
    try {
      const blob = await api.exportPipeline(result.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `map-${result.map_id}.geojson`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error(error.response?.data?.detail || "Export failed"); }
  };

  return (
    <div className="panel p-4" data-testid={TESTIDS.pipelinePanel}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Five-stage map workflow</div>
          <div className="font-heading text-sm flex items-center gap-2"><Layers size={14} className="text-sky-400" />Build a terminal map from vector sources</div>
          <div className="text-[11px] text-slate-500 mt-1">Select one complete package together. Name the broad overall/master plan clearly; file-picker order is not a layer stack.</div>
          <Link to="/help#map-pipeline" className="inline-block text-xs text-sky-300 hover:text-sky-200 mt-1">Map workflow guide</Link>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-[#162032] border border-[#1E293B] cursor-pointer">
            <FileUp size={14} />{files.length === 1 ? files[0].name : files.length > 1 ? `${files.length} files selected` : "Choose files"}
            <input data-testid={TESTIDS.pipelineFileInput} type="file" multiple accept=".svg,.pdf,.dxf,.xlsx" className="hidden" onChange={event => { setFiles(Array.from(event.target.files || [])); setResult(null); }} />
          </label>
          <Select value={sourceType} onValueChange={setSourceType}>
            <SelectTrigger data-testid={TESTIDS.pipelineSourceType} className="w-36 bg-[#050B14] border-[#1E293B] rounded-none h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0E1522] border-[#1E293B] text-slate-100 rounded-none">
              <SelectItem value="auto">Detect file type</SelectItem><SelectItem value="svg">SVG drawings</SelectItem><SelectItem value="vector_pdf">Vector PDFs</SelectItem><SelectItem value="cad">ASCII DXF drawings</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid={TESTIDS.pipelineProcessBtn} size="sm" onClick={run} disabled={busy || !files.length || !terminalId} className="bg-sky-600 rounded-none">
            <Sparkles size={14} className={`mr-1.5 ${busy ? "animate-pulse" : ""}`} />{busy ? "Processing…" : "Process source package"}
          </Button>
        </div>
      </div>

      {result && (
        <div data-testid={TESTIDS.pipelineResultsBlock}>
          <div className="mb-3 flex items-center gap-3 text-xs font-mono">
            <span>{summary.completed}/{summary.total} stages complete</span><span>source: {result.source_type || "detecting"}</span><span className="ml-auto"><StatusBadge status={result.status} /></span>
          </div>
          <div className="space-y-1">{Object.entries(result.stages || {}).map(([name, stage]) => <StageRow key={name} name={name} stage={stage} />)}</div>
          {result.status === "awaiting_review" && <div className="flex justify-end gap-2 pt-3"><Button size="sm" onClick={() => reviewAll("reject")} className="rounded-none bg-red-900/40"><XCircle size={13} className="mr-1" />Reject remaining</Button><Button size="sm" onClick={() => reviewAll("accept")} className="rounded-none bg-emerald-700"><CheckCircle2 size={13} className="mr-1" />Accept valid remaining</Button></div>}
          {result.status === "reviewed" && <div className="flex justify-end pt-3"><Button size="sm" onClick={publish} className="rounded-none bg-sky-600"><Send size={13} className="mr-1" />Publish version</Button></div>}
          {result.status === "published" && <div className="flex justify-end pt-3"><Button size="sm" onClick={exportGeoJson} className="rounded-none bg-[#162032] border border-[#1E293B]"><Download size={13} className="mr-1" />Export GeoJSON</Button></div>}
        </div>
      )}
      {files.length > 1 && <div title={files.map(item => item.name).join(" · ")} className="mb-3 text-[10px] font-mono text-slate-500 whitespace-normal break-words leading-relaxed">{files.map(item => item.name).join(" · ")}</div>}
      {!result && !busy && <div className="text-[11px] text-slate-600 italic mt-2">Prepare: overall/master vector plan → complementary detail plans → optional XLSX. Select them together; the pipeline chooses and reports the base automatically.</div>}
    </div>
  );
}
